// Localização: frontend/src/services/planService.js
// Serviço para mapeamento de planos para módulos (frontend)

/**
 * Mapeamento de planos para módulos disponíveis
 * Deve estar sincronizado com backend/src/services/planService.js
 */
const PLAN_MODULES = {
    // Plano Essencial (ID: 1)
    1: [
        'dashboard', // Dashboard Gestão disponível em todos os planos
        'pacientes',
        'prontuario',
        'usuarios',
        'agendamentos',
        'diario', // Diário de Cuidados disponível em todos os planos
        'equipamentos',
        'check_in_out',
        'planos_medicos'
        // agenda_tarefas removido - disponível apenas em Profissional e Premium
    ],
    // Plano Profissional (ID: 2)
    2: [
        'dashboard',
        'pacientes',
        'prontuario',
        'usuarios',
        'agendamentos',
        'diario', // Diário de Cuidados disponível em todos os planos
        'equipamentos',
        'check_in_out',
        'medicamentos',
        'estoque',
        'planos_medicos',
        'agenda_tarefas',
        'config'
    ],
    // Plano Premium (ID: 3)
    3: [
        'dashboard',
        'pacientes',
        'prontuario',
        'usuarios',
        'agendamentos',
        'diario', // Diário de Cuidados disponível em todos os planos
        'equipamentos',
        'check_in_out',
        'medicamentos',
        'estoque',
        'planos_medicos',
        'agenda_tarefas',
        'config',
        'financeiro',
        'ia' // Assistente de IA
    ]
};

/**
 * Mapeamento reverso: módulo -> plano mínimo necessário
 */
const MODULE_REQUIRED_PLAN = {};
Object.keys(PLAN_MODULES).forEach(planoId => {
    PLAN_MODULES[planoId].forEach(moduleKey => {
        if (!MODULE_REQUIRED_PLAN[moduleKey] || parseInt(planoId) < MODULE_REQUIRED_PLAN[moduleKey]) {
            MODULE_REQUIRED_PLAN[moduleKey] = parseInt(planoId);
        }
    });
});

/**
 * Retorna lista de módulos disponíveis para um plano
 * @param {number} planoId - ID do plano
 * @returns {string[]} Array de módulos disponíveis
 */
export const getAvailableModules = (planoId) => {
    if (!planoId && planoId !== 0) {
        return [];
    }
    return [...(PLAN_MODULES[planoId] || [])];
};

/**
 * Verifica se um módulo está disponível no plano
 * @param {number} planoId - ID do plano
 * @param {string} moduleKey - Chave do módulo
 * @returns {boolean} true se o módulo está disponível
 */
export const isModuleAvailable = (planoId, moduleKey) => {
    if (!planoId || !moduleKey) {
        return false;
    }
    const modules = PLAN_MODULES[planoId] || [];
    return modules.includes(moduleKey);
};

/**
 * Retorna o plano mínimo necessário para acessar um módulo
 * @param {string} moduleKey - Chave do módulo
 * @returns {number|null} ID do plano mínimo necessário
 */
export const getRequiredPlanForModule = (moduleKey) => {
    if (!moduleKey) {
        return null;
    }
    return MODULE_REQUIRED_PLAN[moduleKey] || null;
};

/**
 * Retorna informações do plano necessário para um módulo
 * @param {string} moduleKey - Chave do módulo
 * @returns {object|null} Objeto com { id, nome } ou null
 */
export const getRequiredPlanInfo = (moduleKey) => {
    const planoId = getRequiredPlanForModule(moduleKey);
    if (!planoId) {
        return null;
    }
    
    const planNames = {
        1: 'Essencial',
        2: 'Profissional',
        3: 'Premium'
    };
    
    return {
        id: planoId,
        nome: planNames[planoId] || `Plano ${planoId}`
    };
};

/**
 * Verifica se um plano tem acesso a outro plano (hierarquia)
 * @param {number} userPlanoId - ID do plano do usuário
 * @param {number} requiredPlanoId - ID do plano necessário
 * @returns {boolean} true se o usuário tem plano suficiente
 */
export const hasPlanAccess = (userPlanoId, requiredPlanoId) => {
    if (!userPlanoId || !requiredPlanoId) {
        return false;
    }
    return parseInt(userPlanoId) >= parseInt(requiredPlanoId);
};

